import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Save, AlertTriangle } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import SearchableSelect, { type SearchableOption } from './SearchableSelect';
import { ApiResponse, NsPreset } from '../types';
import Modal from './ui/Modal';

interface Props {
  title: string;
  /** Pre-fill the editor (e.g. a domain's current NS). */
  initial?: string[];
  /** Extra warning line (e.g. "Applies to N domains"). */
  notice?: string;
  busy?: boolean;
  onClose: () => void;
  /** Apply the (validated, lowercased) nameserver list. */
  onSubmit: (nameservers: string[]) => Promise<void>;
}

const BLANK = ['', ''];

const CF_SUFFIX = '.ns.cloudflare.com';

/** Order-insensitive identity for a nameserver set. */
const setKey = (ns: string[]) => [...new Set(ns.map((n) => n.trim().toLowerCase().replace(/\.$/, '')).filter(Boolean))].sort().join(',');

/** "dayana.ns.cloudflare.com, viddy.ns.cloudflare.com" → "dayana / viddy" */
const cfShort = (ns: string[]) => ns.map((n) => n.replace(CF_SUFFIX, '')).join(' / ');

interface CloudflareSet {
  nameservers: string[];
  domain_count: number;
}

export default function NameserverEditModal({ title, initial, notice, busy, onClose, onSubmit }: Props) {
  const { fetchApi, isAdmin } = useApi();
  // Presets are shared across users, so only admins curate them.
  const [rows, setRows] = useState<string[]>(initial && initial.length >= 2 ? initial : initial && initial.length === 1 ? [initial[0], ''] : BLANK);
  const [presets, setPresets] = useState<NsPreset[]>([]);
  const [cloudflare, setCloudflare] = useState<CloudflareSet[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [savingPreset, setSavingPreset] = useState(false);

  const loadPresets = useCallback(async () => {
    try {
      const res = await fetchApi<ApiResponse<NsPreset[]>>('/ns-presets');
      if (res.data) setPresets(res.data);
    } catch { /* non-fatal */ }
  }, [fetchApi]);

  useEffect(() => { loadPresets(); }, [loadPresets]);

  // Cloudflare pairs already in use across the inventory (one pair per
  // Cloudflare account), most used first.
  useEffect(() => {
    fetchApi<ApiResponse<CloudflareSet[]>>('/ns-presets/cloudflare?limit=200')
      .then((res) => { if (res.data) setCloudflare(res.data); })
      .catch(() => { /* non-fatal */ });
  }, [fetchApi]);

  // One option list for the dropdown. Cloudflare pairs that are already saved
  // as presets are shown once, under the preset's name.
  const options = useMemo(() => {
    const saved = presets.map((p) => ({ key: setKey(p.nameservers), preset: p }));
    const savedKeys = new Set(saved.map((o) => o.key));
    const inUse = cloudflare
      .map((c) => ({ key: setKey(c.nameservers), set: c }))
      .filter((o) => !savedKeys.has(o.key));
    return { saved, inUse };
  }, [presets, cloudflare]);

  // Saved presets first, then Cloudflare pairs by how many domains use them.
  // The value is the full hostname set, so typing any part of it matches.
  const selectOptions: SearchableOption[] = useMemo(() => [
    ...options.saved.map((o) => ({
      value: o.key,
      label: `★ ${o.preset.label} — ${o.preset.nameservers.every((n) => n.endsWith(CF_SUFFIX)) ? cfShort(o.preset.nameservers) : o.preset.nameservers.join(', ')}`,
    })),
    ...options.inUse.map((o) => ({
      value: o.key,
      label: `${cfShort(o.set.nameservers)} · ${o.set.domain_count} domain${o.set.domain_count === 1 ? '' : 's'}`,
    })),
  ], [options]);

  // Reflect the current rows in the dropdown when they match an option.
  const currentKey = setKey(rows);
  const selectedPreset = options.saved.find((o) => o.key === currentKey)?.preset ?? null;
  const selectValue = selectedPreset || options.inUse.some((o) => o.key === currentKey) ? currentKey : '';

  const applyKey = (key: string) => {
    if (!key) return;
    const ns = options.saved.find((o) => o.key === key)?.preset.nameservers
      ?? options.inUse.find((o) => o.key === key)?.set.nameservers;
    if (ns) applyPreset(ns);
  };

  const setRow = (i: number, v: string) => setRows((r) => r.map((x, idx) => (idx === i ? v : x)));
  const addRow = () => setRows((r) => [...r, '']);
  const removeRow = (i: number) => setRows((r) => (r.length <= 2 ? r : r.filter((_, idx) => idx !== i)));

  const clean = () => rows.map((r) => r.trim().toLowerCase()).filter(Boolean);

  const applyPreset = (ns: string[]) => {
    setError(null);
    setRows(ns.length >= 2 ? [...ns] : [...ns, ''].slice(0, Math.max(2, ns.length)));
  };

  const saveAsPreset = async () => {
    const ns = clean();
    if (ns.length < 2) { setError('Enter at least 2 nameservers before saving a preset.'); return; }
    const label = window.prompt('Preset name (e.g. "Cloudflare — acme account"):')?.trim();
    if (!label) return;
    setSavingPreset(true);
    try {
      await fetchApi('/ns-presets', { method: 'POST', body: JSON.stringify({ label, nameservers: ns }) });
      await loadPresets();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save preset');
    } finally {
      setSavingPreset(false);
    }
  };

  const deletePreset = async (id: string) => {
    try { await fetchApi(`/ns-presets/${id}`, { method: 'DELETE' }); await loadPresets(); } catch { /* ignore */ }
  };

  const submit = async () => {
    const ns = clean();
    if (ns.length < 2) { setError('At least 2 nameservers are required.'); return; }
    setError(null);
    try {
      await onSubmit(ns);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <Modal
      title={title}
      size="lg"
      onClose={onClose}
      bodyClassName="p-5 space-y-4"
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary text-sm" disabled={busy}>Cancel</button>
          <button onClick={submit} className="btn btn-primary text-sm" disabled={busy}>{busy ? 'Updating…' : 'Update nameservers'}</button>
        </>
      }
    >
          {notice && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{notice} This pushes a live DNS change at the registrar.</span>
            </div>
          )}

          {/* Preset dropdown: saved presets + Cloudflare pairs in use */}
          {(options.saved.length > 0 || options.inUse.length > 0) && (
            <div>
              <label className="text-xs font-medium text-slate-500">Quick fill</label>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1" id="ns-preset">
                  <SearchableSelect
                    value={selectValue}
                    onChange={applyKey}
                    options={selectOptions}
                    allLabel="Choose a preset or Cloudflare pair…"
                    placeholder="Type a name, e.g. dayana"
                  />
                </div>
                {isAdmin && selectedPreset && (
                  <button onClick={() => deletePreset(selectedPreset.id)} className="text-slate-400 hover:text-red-500" title={`Delete preset "${selectedPreset.label}"`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              {options.inUse.length > 0 && (
                <p className="text-[11px] text-slate-400 mt-1">Cloudflare assigns one pair per account — pick the pair of the account the zone was added to.</p>
              )}
            </div>
          )}

          {/* NS rows */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-500">Nameservers</label>
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={r}
                  onChange={(e) => setRow(i, e.target.value)}
                  placeholder={i === 0 ? 'ns1.example.com' : 'ns2.example.com'}
                  className="input flex-1 font-mono text-sm"
                />
                <button onClick={() => removeRow(i)} disabled={rows.length <= 2} className="text-slate-400 hover:text-red-500 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <button onClick={addRow} className="text-xs text-brand-600 hover:text-brand-800 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add nameserver</button>
              {isAdmin && <button onClick={saveAsPreset} disabled={savingPreset} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"><Save className="h-3.5 w-3.5" /> Save as preset</button>}
            </div>
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}
    </Modal>
  );
}
